(() => {
  'use strict';

  const CONFIG_ELEMENT_ID = 'WishlistConfig';
  const MAX_ITEMS = 200;
  const FETCH_CONCURRENCY = 4;

  function readConfig() {
    const element = document.getElementById(CONFIG_ELEMENT_ID);
    if (!element) return null;

    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      console.warn('[Wishlist] Invalid configuration.', error);
      return null;
    }
  }


  /*
   * Persistence adapter.
   */
  class LocalWishlistStore {
    constructor(storageKey) {
      this.storageKey = storageKey;
      this.listeners = new Set();
      this.items = this.read();

      window.addEventListener('storage', (event) => {
        if (event.key !== this.storageKey) return;

        this.items = this.read();
        this.notify({ type: 'external' });
      });
    }


    normalizeItems(value) {
      const source = Array.isArray(value)
        ? value
        : Array.isArray(value?.items)
          ? value.items
          : [];

      const seen = new Set();
      const normalized = [];

      for (const rawItem of source) {
        const id = String(rawItem?.id || '').trim();
        const handle = String(rawItem?.handle || '').trim();
        const addedAt = Number(rawItem?.addedAt) || Date.now();

        if (!id || !handle || seen.has(id)) continue;

        seen.add(id);

        normalized.push({
          id,
          handle,
          addedAt,
        });

        if (normalized.length >= MAX_ITEMS) break;
      }

      return normalized;
    }


    read() {
      try {
        const raw = localStorage.getItem(this.storageKey);

        if (!raw) return [];

        return this.normalizeItems(JSON.parse(raw));
      } catch (error) {
        console.warn('[Wishlist] Could not read localStorage.', error);
        return [];
      }
    }


    persist(change) {
      const payload = {
        version: 1,
        updatedAt: Date.now(),
        items: this.items,
      };

      try {
        localStorage.setItem(
          this.storageKey,
          JSON.stringify(payload)
        );
      } catch (error) {
        console.warn('[Wishlist] Could not write localStorage.', error);
      }

      this.notify(change);
    }


    subscribe(listener) {
      this.listeners.add(listener);

      return () => this.listeners.delete(listener);
    }


    notify(change) {
      for (const listener of this.listeners) {
        listener(change, this.getItems());
      }
    }


    getItems() {
      return this.items.map((item) => ({ ...item }));
    }


    count() {
      return this.items.length;
    }


    has(productId) {
      const id = String(productId || '');

      return this.items.some((item) => item.id === id);
    }


    add(item) {
      const id = String(item?.id || '').trim();
      const handle = String(item?.handle || '').trim();

      if (!id || !handle) return false;

      const existingIndex = this.items.findIndex(
        (entry) => entry.id === id
      );

      if (existingIndex !== -1) {
        const existing = this.items[existingIndex];

        if (existing.handle !== handle) {
          this.items[existingIndex] = {
            ...existing,
            handle,
          };

          this.persist({
            type: 'update',
            item: { ...this.items[existingIndex] },
          });
        }

        return false;
      }

      const wishlistItem = {
        id,
        handle,
        addedAt: Date.now(),
      };

      this.items = [
        wishlistItem,
        ...this.items,
      ].slice(0, MAX_ITEMS);

      this.persist({
        type: 'add',
        item: { ...wishlistItem },
      });

      return true;
    }


    remove(productId) {
      const id = String(productId || '').trim();

      const existing = this.items.find(
        (item) => item.id === id
      );

      if (!existing) return false;

      this.items = this.items.filter(
        (item) => item.id !== id
      );

      this.persist({
        type: 'remove',
        item: { ...existing },
      });

      return true;
    }


    toggle(item) {
      if (this.has(item?.id)) {
        this.remove(item.id);
        return false;
      }

      this.add(item);
      return true;
    }
  }


  class WishlistPage {
    constructor(root, store, config) {
      this.root = root;
      this.store = store;
      this.config = config;

      this.grid = root.querySelector('[data-wishlist-grid]');
      this.toolbar = root.querySelector('[data-wishlist-toolbar]');
      this.empty = root.querySelector('[data-wishlist-empty]');
      this.loading = root.querySelector('[data-wishlist-loading]');
      this.error = root.querySelector('[data-wishlist-error]');
      this.count = root.querySelector('[data-wishlist-product-count]');
      this.sort = root.querySelector('[data-wishlist-sort]');

      this.renderGeneration = 0;

      this.collator = new Intl.Collator(
        document.documentElement.lang || undefined,
        {
          sensitivity: 'base',
          numeric: true,
        }
      );
    }


    init() {
      if (!this.grid) return;

      this.sort?.addEventListener(
        'change',
        () => this.sortCards()
      );

      this.store.subscribe(
        (change) => this.onStoreChange(change)
      );

      this.render();
    }


    onStoreChange(change) {
      /*
       * Removal on the wishlist page is intentionally local.
       * Do not download every remaining product again.
       */
      if (change?.type === 'remove') {
        this.removeRenderedCard(change.item.id);
        this.updateChrome();
        return;
      }

      if (change?.type === 'update') return;

      /*
       * Adds or external-tab changes require a new render.
       */
      this.render();
    }


    updateChrome() {
      const total = this.store.count();
      const strings = this.config.strings;

      if (this.count) {
        this.count.textContent =
          `${total} ${
            total === 1
              ? strings.productSingular
              : strings.productPlural
          }`;
      }

      if (total === 0) {
        if (this.grid) {
          this.grid.innerHTML = '';
          this.grid.setAttribute('aria-busy', 'false');
        }

        if (this.toolbar) {
          this.toolbar.hidden = true;
        }

        if (this.empty) {
          this.empty.hidden = false;
        }

        if (this.loading) {
          this.loading.hidden = true;
        }

        if (this.error) {
          this.error.hidden = true;
        }

        this.root.classList.remove(
          'wishlist-page--loading'
        );
      } else {
        if (this.toolbar) {
          this.toolbar.hidden = false;
        }

        if (this.empty) {
          this.empty.hidden = true;
        }
      }
    }


    removeRenderedCard(productId) {
      if (!this.grid) return;

      const cards = this.grid.querySelectorAll(
        '[data-wishlist-rendered-card]'
      );

      const card = Array.from(cards).find(
        (element) =>
          element.dataset.wishlistItemId === String(productId)
      );

      if (!card) return;

      card.classList.add('wishlist-card--removing');

      window.setTimeout(
        () => card.remove(),
        180
      );
    }


    createPlaceholder(item) {
      const placeholder = document.createElement('li');

      placeholder.className =
        'grid__item wishlist-card-placeholder';

      placeholder.dataset.wishlistPlaceholderId = item.id;

      placeholder.setAttribute(
        'aria-hidden',
        'true'
      );

      placeholder.innerHTML = `
        <div class="wishlist-card-placeholder__media"></div>

        <div class="wishlist-card-placeholder__line wishlist-card-placeholder__line--wide"></div>

        <div class="wishlist-card-placeholder__rating"></div>

        <div class="wishlist-card-placeholder__line wishlist-card-placeholder__line--medium"></div>

        <div class="wishlist-card-placeholder__line wishlist-card-placeholder__line--short"></div>

        <div class="wishlist-card-placeholder__button"></div>
      `;

      return placeholder;
    }


    productSectionUrl(handle) {
      const root = String(
        window.Shopify?.routes?.root ||
        this.root.dataset.wishlistRoot ||
        '/'
      );

      const normalizedRoot = root.endsWith('/')
        ? root
        : `${root}/`;

      return (
        `${normalizedRoot}products/` +
        `${encodeURIComponent(handle)}` +
        `?section_id=wishlist-product-card`
      );
    }


    async fetchCard(item) {
      const response = await fetch(
        this.productSectionUrl(item.handle),
        {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();

      const documentFragment =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const card =
        documentFragment.querySelector(
          '[data-wishlist-rendered-card]'
        );

      if (!card) {
        throw new Error(
          'Wishlist card markup missing'
        );
      }

      const imported =
        document.importNode(card, true);

      imported.dataset.wishlistAddedAt =
        String(item.addedAt);

      return imported;
    }


    async render() {
      const generation =
        ++this.renderGeneration;

      const items =
        this.store.getItems();

      this.updateChrome();

      if (items.length === 0) {
        return;
      }


      this.root.classList.add(
        'wishlist-page--loading'
      );

      this.grid.setAttribute(
        'aria-busy',
        'true'
      );

      if (this.loading) {
        this.loading.hidden = false;
      }

      if (this.error) {
        this.error.hidden = true;
      }


      /*
       * Replace the generic Liquid skeletons once with the exact
       * number of wishlist placeholders.
       *
       * These placeholders then remain untouched until ALL product
       * section requests have finished.
       */
      const placeholders =
        items.map(
          (item) => this.createPlaceholder(item)
        );

      this.grid.replaceChildren(
        ...placeholders
      );


      /*
       * Results are stored by original wishlist index.
       * Nothing is inserted into the live DOM while requests
       * are still running.
       */
      const results =
        new Array(items.length).fill(null);


      let cursor = 0;
      let failures = 0;


      const worker = async () => {
        while (cursor < items.length) {
          const index = cursor++;
          const item = items[index];

          try {
            results[index] =
              await this.fetchCard(item);

          } catch (error) {
            failures += 1;

            console.warn(
              `[Wishlist] Could not render ${item.handle}.`,
              error
            );
          }
        }
      };


      const workers = Array.from(
        {
          length: Math.min(
            FETCH_CONCURRENCY,
            items.length
          ),
        },
        () => worker()
      );


      await Promise.all(workers);


      /*
       * Ignore a completed request batch if a newer wishlist
       * render started while it was loading.
       */
      if (
        generation !==
        this.renderGeneration
      ) {
        return;
      }


      /*
       * Keep the skeleton grid's current height during the DOM swap.
       * This prevents the page below it from jumping during insertion.
       */
      const reservedHeight =
        this.grid.getBoundingClientRect().height;

      if (reservedHeight > 0) {
        this.grid.style.minHeight =
          `${reservedHeight}px`;
      }


      const fragment =
        document.createDocumentFragment();


      for (const card of results) {
        if (card) {
          fragment.appendChild(card);
        }
      }


      /*
       * One DOM replacement.
       *
       * No progressive:
       * image -> text -> image -> text -> button...
       * reflows while individual fetches finish.
       */
      this.grid.replaceChildren(fragment);


      /*
       * Apply the selected sort while the temporary reserved
       * grid height is still active.
       */
      this.sortCards();


      this.grid.setAttribute(
        'aria-busy',
        'false'
      );


      if (this.loading) {
        this.loading.hidden = true;
      }

      if (this.error) {
        this.error.hidden =
          failures === 0;
      }


      /*
       * Let the final card layout paint first, then release the
       * temporary skeleton height reservation.
       */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.grid.style.minHeight = '';

          this.root.classList.remove(
            'wishlist-page--loading'
          );
        });
      });
    }


    sortCards() {
      if (!this.grid) return;

      const mode =
        this.sort?.value || 'newest';

      const cards = Array.from(
        this.grid.querySelectorAll(
          '[data-wishlist-rendered-card]'
        )
      );


      const number = (card, key) =>
        Number(card.dataset[key]) || 0;

      const text = (card, key) =>
        String(card.dataset[key] || '');


      cards.sort((a, b) => {
        switch (mode) {
          case 'oldest':
            return (
              number(a, 'wishlistAddedAt') -
              number(b, 'wishlistAddedAt')
            );

          case 'price-asc':
            return (
              number(a, 'wishlistPrice') -
              number(b, 'wishlistPrice')
            );

          case 'price-desc':
            return (
              number(b, 'wishlistPrice') -
              number(a, 'wishlistPrice')
            );

          case 'title-asc':
            return this.collator.compare(
              text(a, 'wishlistTitle'),
              text(b, 'wishlistTitle')
            );

          case 'title-desc':
            return this.collator.compare(
              text(b, 'wishlistTitle'),
              text(a, 'wishlistTitle')
            );

          case 'newest':
          default:
            return (
              number(b, 'wishlistAddedAt') -
              number(a, 'wishlistAddedAt')
            );
        }
      });


      for (const card of cards) {
        this.grid.appendChild(card);
      }
    }
  }


  class WishlistController {
    constructor(store, config) {
      this.store = store;
      this.config = config;
      this.observer = null;
    }


    init() {
      /*
       * One delegated listener handles existing and future
       * section-rendered product cards.
       */
      document.addEventListener(
        'click',
        (event) => this.handleClick(event)
      );


      this.store.subscribe(
        () => this.refresh(document)
      );


      this.refresh(document);
      this.observeDynamicContent();


      const page =
        document.querySelector(
          '[data-wishlist-page]'
        );


      if (page) {
        new WishlistPage(
          page,
          this.store,
          this.config
        ).init();
      }
    }


    handleClick(event) {
      if (
        !(event.target instanceof Element)
      ) {
        return;
      }


      const button =
        event.target.closest(
          '[data-wishlist-toggle]'
        );


      if (!button) return;


      event.preventDefault();
      event.stopPropagation();


      const id = String(
        button.dataset.productId || ''
      ).trim();


      const handle = String(
        button.dataset.productHandle || ''
      ).trim();


      if (!id || !handle) return;


      this.store.toggle({
        id,
        handle,
      });


      /*
       * Release pointer-generated focus so Dawn's normal
       * button shadow returns immediately.
       *
       * Keyboard-generated clicks have detail === 0.
       */
      if (event.detail > 0) {
        requestAnimationFrame(() => {
          button.blur();
        });
      }
    }


    refresh(root) {
      const scope =
        root instanceof Element ||
        root instanceof Document
          ? root
          : document;


      const buttons = [];


      if (
        scope instanceof Element &&
        scope.matches('[data-wishlist-toggle]')
      ) {
        buttons.push(scope);
      }


      buttons.push(
        ...scope.querySelectorAll(
          '[data-wishlist-toggle]'
        )
      );


      for (const button of buttons) {
        const active =
          this.store.has(
            button.dataset.productId
          );


        const label = active
          ? this.config.strings.remove
          : this.config.strings.add;


        button.classList.toggle(
          'is-active',
          active
        );


        button.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );


        button.setAttribute(
          'aria-label',
          label
        );


        button.setAttribute(
          'title',
          label
        );


        const visibleLabel =
          button.querySelector(
            '[data-wishlist-button-label]'
          );


        if (visibleLabel) {
          visibleLabel.textContent =
            label;
        }
      }


      this.refreshHeader();
    }


    refreshHeader() {
      const count =
        this.store.count();


      const strings =
        this.config.strings;


      const countText =
        `${count} ${
          count === 1
            ? strings.productSingular
            : strings.productPlural
        }`;


      for (
        const value of
        document.querySelectorAll(
          '[data-wishlist-count]'
        )
      ) {
        value.textContent =
          String(count);
      }


      for (
        const bubble of
        document.querySelectorAll(
          '[data-wishlist-count-bubble]'
        )
      ) {
        bubble.hidden =
          count === 0;
      }


      for (
        const label of
        document.querySelectorAll(
          '[data-wishlist-count-label]'
        )
      ) {
        label.textContent =
          countText;
      }


      for (
        const link of
        document.querySelectorAll(
          '[data-wishlist-header-link]'
        )
      ) {
        link.classList.toggle(
          'has-items',
          count > 0
        );


        link.setAttribute(
          'aria-label',
          count > 0
            ? `${strings.wishlist}: ${countText}`
            : strings.wishlist
        );
      }
    }


    /*
     * Dawn filtering/sorting and Shopify sections can replace
     * product-card HTML dynamically.
     *
     * Observe only newly-added wishlist controls.
     */
    observeDynamicContent() {
      let scheduled = false;
      const pendingRoots = new Set();


      this.observer =
        new MutationObserver(
          (mutations) => {

            for (const mutation of mutations) {
              for (
                const node of
                mutation.addedNodes
              ) {

                if (
                  !(node instanceof Element)
                ) {
                  continue;
                }


                if (
                  node.matches(
                    '[data-wishlist-toggle]'
                  ) ||
                  node.querySelector(
                    '[data-wishlist-toggle]'
                  )
                ) {
                  pendingRoots.add(node);
                }
              }
            }


            if (
              scheduled ||
              pendingRoots.size === 0
            ) {
              return;
            }


            scheduled = true;


            requestAnimationFrame(() => {
              for (
                const root of pendingRoots
              ) {
                this.refresh(root);
              }


              pendingRoots.clear();
              scheduled = false;
            });
          }
        );


      this.observer.observe(
        document.body,
        {
          childList: true,
          subtree: true,
        }
      );
    }
  }


  const config =
    readConfig();


  if (
    !config?.storageKey ||
    !config?.strings
  ) {
    return;
  }


  const store =
    new LocalWishlistStore(
      config.storageKey
    );


  const controller =
    new WishlistController(
      store,
      config
    );


  window.LumissenceWishlist = {
    store,

    getItems: () =>
      store.getItems(),

    has: (productId) =>
      store.has(productId),

    add: (item) =>
      store.add(item),

    remove: (productId) =>
      store.remove(productId),
  };


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => controller.init(),
      { once: true }
    );
  } else {
    controller.init();
  }
})();